import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, 
  Target, 
  Users, 
  Award, 
  CheckCircle2, 
  ArrowRight,
  Search,
  Settings,
  FileText,
  Link2,
  BarChart3,
  Zap,
  Shield,
  Clock,
  ChevronRight,
  Send,
  Menu,
  X,
  Quote,
  Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { CountryCodeSelect } from "../components/ui/country-code-select";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    // window.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "https://itmunch.com/", label: "Home", external: true },
    { href: "#services", label: "Services" },
    { href: "#process", label: "Process" },
    { href: "#pricing", label: "Pricing" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="absolute top-0 left-0 right-0 z-50 bg-transparent"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-24">
          <a href="https://itmunch.com/" className="flex items-center" data-testid="nav-logo">
            <img 
              src="https://itmunch.com/wp-content/uploads/2021/01/ITT-MUNCH_LOGO-4-1-1024x512_800x400.webp" 
              alt="iTMunch - Where Technology Meets You"
              className="h-20 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-gray-300 hover:text-itmunch-cyan transition-colors font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact">
              <Button 
                className="bg-itmunch-cyan text-itmunch-navy hover:brightness-110 rounded-full px-6 btn-shine"
                data-testid="nav-cta-button"
              >
                Get Started
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="md:hidden bg-itmunch-dark/95 backdrop-blur-md pb-6"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-gray-300 hover:text-itmunch-cyan transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full mt-4 bg-itmunch-cyan text-itmunch-navy rounded-full">
                Get Started
              </Button>
            </a>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-screen bg-[#132d4a] overflow-hidden" data-testid="hero-section">
    {/* Lighter Background Layer */}
    <div className="absolute inset-0 bg-gradient-to-b from-[#1a3a5c] via-[#132d4a] to-[#0d1e36]" />
    
    {/* Animated Background */}
    <div className="absolute inset-0 bg-grid-pattern opacity-15" />
    <div className="absolute top-1/4 right-0 w-96 h-96 bg-itmunch-cyan/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-itmunch-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
    
    {/* Animated Globe CSS */}
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="globe-container">
        <div className="globe-wireframe" />
      </div>
    </div>
    
    <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
        {/* Left Content */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-left"
        >
          <motion.div variants={fadeUp} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-itmunch-cyan/10 rounded-full text-itmunch-cyan text-sm font-medium border border-itmunch-cyan/20">
              <div className="w-2 h-2 bg-itmunch-cyan rounded-full animate-ping" />
              Enterprise Grade SEO & GEO Solutions
            </span>
          </motion.div>
          
          <motion.h1 
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
          >
            Dominate Search.
            <br />
            <span className="text-itmunch-cyan relative">
              Scale Revenue.
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
                className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-itmunch-cyan via-itmunch-cyan/80 to-transparent rounded-full"
              />
            </span>
          </motion.h1>
          
          <motion.p 
            variants={fadeUp}
            className="text-gray-400 text-lg mb-4 max-w-xl"
          >
            Enterprise-grade SEO & GEO strategies for brands that demand growth. 
            Data-driven optimization that delivers measurable ROI.
          </motion.p>

          <motion.div 
            variants={fadeUp}
            className="flex items-center gap-3 mb-8 px-4 py-3 bg-white/5 rounded-xl border border-itmunch-cyan/20 max-w-xl"
          >
            <Zap className="text-itmunch-cyan shrink-0" size={20} />
            <p className="text-gray-300 text-sm">
              <span className="text-itmunch-cyan font-semibold">NEW: GEO</span> — Get your brand recommended by ChatGPT, Gemini, Perplexity & Claude
            </p>
          </motion.div>
          
          <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
            <a href="#contact">
              <Button 
                size="lg" 
                className="bg-itmunch-cyan text-itmunch-navy hover:brightness-110 rounded-full px-8 text-lg btn-shine group"
                data-testid="hero-cta-button"
              >
                Get Free Consultation 
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </a>
            <a href="#process">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-gray-600 text-white hover:bg-white/5 rounded-full px-8 group"
                data-testid="hero-secondary-button"
              >
                <Zap className="mr-2 group-hover:scale-110 transition-transform" size={18} />
                See Our Process
              </Button>
            </a>
          </motion.div>
        </motion.div>

        {/* Right - Floating Stats */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative hidden lg:flex justify-center items-center min-h-[400px]"
        >
          {/* Floating Stats Cards */}
          <motion.div 
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-0 right-10 glass-card rounded-xl p-5 border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-itmunch-cyan/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-itmunch-cyan" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Average ROI</p>
                <p className="text-white text-xl font-heading font-bold">+300%</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="absolute bottom-10 left-0 glass-card rounded-xl p-5 border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-itmunch-cyan/20 rounded-lg flex items-center justify-center">
                <Users className="text-itmunch-cyan" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Global Clients</p>
                <p className="text-white text-xl font-heading font-bold">200+</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-32 right-0 glass-card rounded-xl p-5 border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-itmunch-cyan/20 rounded-lg flex items-center justify-center">
                <Award className="text-itmunch-cyan" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Retention Rate</p>
                <p className="text-white text-xl font-heading font-bold">98%</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            className="absolute top-20 left-10 glass-card rounded-xl p-5 border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-itmunch-cyan/20 rounded-lg flex items-center justify-center">
                <Target className="text-itmunch-cyan" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Projects</p>
                <p className="text-white text-xl font-heading font-bold">500+</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: "500+", label: "SEO Projects Delivered", icon: Target },
    { value: "200+", label: "Clients Worldwide", icon: Users },
    { value: "98%", label: "Client Retention Rate", icon: Award },
    { value: "300%", label: "Average ROI Increase", icon: TrendingUp }
  ];

  return (
    <section className="py-16 bg-white" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {stats.map((stat, index) => (
            <motion.div 
              key={index}
              variants={fadeUp}
              className="text-center"
              data-testid={`stat-${index}`}
            >
              <stat.icon className="mx-auto mb-3 text-itmunch-cyan" size={32} />
              <p className="text-4xl lg:text-5xl font-heading font-bold text-itmunch-navy stat-number">
                {stat.value}
              </p>
              <p className="text-gray-500 mt-2">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section (Bento Grid)
const ServicesSection = () => {
  const services = [
    {
      title: "Technical SEO",
      description: "Site architecture, speed optimization, crawlability, and indexation improvements.",
      icon: Settings,
      span: "lg:col-span-2 lg:row-span-2"
    },
    {
      title: "Content Strategy",
      description: "Keyword-optimized content that ranks and converts.",
      icon: FileText,
      span: ""
    },
    {
      title: "Link Building",
      description: "High-authority backlinks that boost domain rating.",
      icon: Link2,
      span: ""
    },
    {
      title: "GEO - AI Visibility",
      description: "Optimize for AI platforms like ChatGPT, Gemini, Perplexity & Claude to get your brand recommended.",
      icon: Zap,
      span: "lg:col-span-2"
    },
    {
      title: "Analytics & Reporting",
      description: "Real-time dashboards, monthly reports, and ROI tracking.",
      icon: BarChart3,
      span: "lg:col-span-2"
    }
  ];

  return (
    <section id="services" className="py-20 lg:py-32 bg-itmunch-light" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-itmunch-navy mb-4">
            Professional SEO Optimization Services
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Comprehensive strategies to improve your website's visibility and drive sustainable growth.
          </p>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className={`bg-white rounded-2xl p-8 hover-lift ${service.span}`}
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-itmunch-cyan/10 rounded-xl flex items-center justify-center mb-6">
                <service.icon className="text-itmunch-cyan" size={28} />
              </div>
              <h3 className="text-xl font-heading font-semibold text-itmunch-navy mb-3">
                {service.title}
              </h3>
              <p className="text-gray-500">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      num: "01",
      title: "Comprehensive SEO Audit",
      description: "In-depth analysis of your website, competitors, and industry landscape."
    },
    {
      num: "02",
      title: "Strategic Planning",
      description: "Customized roadmap with keyword research and content strategies."
    },
    {
      num: "03",
      title: "Technical Implementation",
      description: "On-page and technical optimizations using advanced SEO tools."
    },
    {
      num: "04",
      title: "Content Enhancement",
      description: "High-quality content targeting strategic keywords."
    },
    {
      num: "05",
      title: "Monitor & Optimize",
      description: "Continuous tracking and refinement for maximum ROI."
    }
  ];

  return (
    <section id="process" className="py-20 lg:py-32 bg-itmunch-dark" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
            Our SEO Optimization Process
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            A proven methodology for sustainable search engine success.
          </p>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="glass-card rounded-2xl p-6 relative"
              data-testid={`process-step-${index}`}
            >
              <span className="text-5xl font-heading font-bold text-itmunch-cyan/20">
                {step.num}
              </span>
              <h3 className="text-lg font-heading font-semibold text-white mt-4 mb-2">
                {step.title}
              </h3>
              <p className="text-gray-400 text-sm">{step.description}</p>
              {index < steps.length - 1 && (
                <ChevronRight className="hidden lg:block absolute top-1/2 -right-4 text-itmunch-cyan/50" size={24} />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// GEO Section - AI Visibility
const GEOSection = () => (
  <section className="py-20 lg:py-32 bg-gradient-to-b from-itmunch-dark to-[#0d1e36]" data-testid="geo-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-itmunch-cyan/10 rounded-full text-itmunch-cyan text-sm font-medium border border-itmunch-cyan/20 mb-6">
            <Zap size={16} /> NEW: Generative Engine Optimization
          </span>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-6">
            Get Your Brand Recommended by AI
          </h2>
          <p className="text-gray-400 mb-6">
            <strong className="text-white">GEO (Generative Engine Optimization)</strong> is the next frontier of digital visibility. 
            As users increasingly turn to AI assistants for recommendations, your brand needs to be optimized 
            for these new discovery channels.
          </p>
          <p className="text-gray-400 mb-8">
            We help your business get mentioned and recommended by leading AI platforms, 
            driving qualified traffic from users who trust AI-powered suggestions.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: "ChatGPT", desc: "OpenAI's Assistant" },
              { name: "Gemini", desc: "Google's AI" },
              { name: "Perplexity", desc: "AI Search Engine" },
              { name: "Claude", desc: "Anthropic's AI" }
            ].map((platform, index) => (
              <div key={index} className="glass-card rounded-xl p-4 border border-white/10">
                <p className="text-white font-semibold">{platform.name}</p>
                <p className="text-gray-400 text-sm">{platform.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="space-y-6"
        >
          <motion.div variants={fadeUp} className="glass-card rounded-2xl p-6 border border-itmunch-cyan/20">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-itmunch-cyan/20 rounded-xl flex items-center justify-center shrink-0">
                <Target className="text-itmunch-cyan" size={24} />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">AI-First Content Strategy</h3>
                <p className="text-gray-400 text-sm">Structure your content to be easily understood and cited by AI models, increasing your chances of being recommended.</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div variants={fadeUp} className="glass-card rounded-2xl p-6 border border-white/10">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-itmunch-cyan/20 rounded-xl flex items-center justify-center shrink-0">
                <Award className="text-itmunch-cyan" size={24} />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Authority Building</h3>
                <p className="text-gray-400 text-sm">Establish your brand as an authoritative source that AI platforms trust and recommend to users.</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div variants={fadeUp} className="glass-card rounded-2xl p-6 border border-white/10">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-itmunch-cyan/20 rounded-xl flex items-center justify-center shrink-0">
                <TrendingUp className="text-itmunch-cyan" size={24} />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Future-Proof Visibility</h3>
                <p className="text-gray-400 text-sm">Stay ahead of the curve as AI-powered search becomes the primary way users discover businesses.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Why Choose Us Section
const WhyChooseSection = () => {
  const reasons = [
    {
      title: "Proven Track Record",
      description: "500+ successful projects with 300% average ROI increase.",
      icon: Award
    },
    {
      title: "Advanced Automation",
      description: "Cutting-edge SEO technology combined with expert oversight.",
      icon: Zap
    },
    {
      title: "Data-Driven Strategies",
      description: "Every decision backed by analytics for maximum impact.",
      icon: BarChart3
    },
    {
      title: "Transparent Delivery",
      description: "Real-time reporting and measurable results from day one.",
      icon: Shield
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-itmunch-navy mb-6">
              Why Choose iTMunch as Your SEO Partner?
            </h2>
            <p className="text-gray-500 mb-8">
              Unmatched expertise and results-driven SEO optimization services 
              that deliver measurable growth for your business.
            </p>
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
              alt="Professional SEO Team"
              className="rounded-2xl shadow-lg"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {reasons.map((reason, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="bg-itmunch-light rounded-2xl p-6 hover-lift"
                data-testid={`reason-card-${index}`}
              >
                <div className="w-12 h-12 bg-itmunch-cyan/10 rounded-xl flex items-center justify-center mb-4">
                  <reason.icon className="text-itmunch-cyan" size={24} />
                </div>
                <h3 className="text-lg font-heading font-semibold text-itmunch-navy mb-2">
                  {reason.title}
                </h3>
                <p className="text-gray-500 text-sm">{reason.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const [pageCount, setPageCount] = useState([50]);
  
  const calculatePrice = (basePrice, includedPages) => {
    const extraPages = Math.max(0, pageCount[0] - includedPages);
    const extraCost = extraPages > 0 ? extraPages * 1 : 0;
    return basePrice + extraCost;
  };

  const plans = [
    {
      name: "Starter",
      basePrice: 300,
      includedPages: 50,
      description: "Perfect for small businesses starting with SEO",
      features: [
        "Up to 50 pages optimized",
        "Keyword research (30 keywords)",
        "On-page SEO optimization",
        "Basic GEO setup for 1 AI platform",
        "Monthly performance report",
        "Email support"
      ],
      highlighted: false
    },
    {
      name: "Growth",
      basePrice: 800,
      includedPages: 100,
      description: "Ideal for growing businesses scaling their presence",
      features: [
        "Up to 100 pages optimized",
        "Keyword research (75 keywords)",
        "Technical SEO audit & fixes",
        "Content strategy & creation",
        "GEO optimization for ChatGPT & Gemini",
        "Link building (10 links/month)",
        "Bi-weekly reports",
        "Priority support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      basePrice: 1500,
      includedPages: 200,
      description: "Complete SEO solution for large organizations",
      features: [
        "Up to 200 pages optimized",
        "Unlimited keyword research",
        "Advanced technical SEO",
        "Content marketing strategy",
        "Full GEO: ChatGPT, Gemini, Perplexity & Claude",
        "Link building (25 links/month)",
        "Competitor analysis",
        "Weekly reports & calls",
        "Dedicated account manager"
      ],
      highlighted: false
    }
  ];

  return (
    <section id="pricing" className="py-20 lg:py-32 bg-itmunch-light" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-itmunch-navy mb-4">
            Transparent Pricing Plans
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Flexible plans designed to fit your business needs. 
            Extra pages above plan limits: $1/page/month.
          </p>
        </motion.div>

        {/* Page Calculator */}
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="max-w-xl mx-auto mb-12 bg-white rounded-2xl p-6 shadow-sm"
        >
          <div className="flex justify-between items-center mb-4">
            <span className="text-itmunch-navy font-medium">Number of pages:</span>
            <span className="text-2xl font-heading font-bold text-itmunch-cyan">{pageCount[0]}</span>
          </div>
          <Slider
            value={pageCount}
            onValueChange={setPageCount}
            max={300}
            min={50}
            step={10}
            className="w-full"
            data-testid="page-count-slider"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-2">
            <span>50 pages</span>
            <span>300 pages</span>
          </div>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className={`bg-white rounded-2xl p-8 ${
                plan.highlighted ? "pricing-highlight" : "border border-gray-100"
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {plan.highlighted && (
                <span className="inline-block bg-itmunch-cyan text-itmunch-navy text-xs font-bold px-3 py-1 rounded-full mb-4">
                  MOST POPULAR
                </span>
              )}
              <h3 className="text-2xl font-heading font-bold text-itmunch-navy">{plan.name}</h3>
              <p className="text-gray-500 text-sm mt-2 mb-4">{plan.description}</p>
              <div className="mb-6">
                <span className="text-4xl font-heading font-bold text-itmunch-navy">
                  ${calculatePrice(plan.basePrice, plan.includedPages)}
                </span>
                <span className="text-gray-400">/month</span>
                {pageCount[0] > plan.includedPages && (
                  <p className="text-xs text-itmunch-cyan mt-1">
                    +${pageCount[0] - plan.includedPages} for extra pages
                  </p>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                    <CheckCircle2 className="text-itmunch-cyan shrink-0 mt-0.5" size={16} />
                    {feature}
                  </li>
                ))}
              </ul>
              <a href="#contact">
                <Button 
                  className={`w-full rounded-full ${
                    plan.highlighted 
                      ? "bg-itmunch-cyan text-itmunch-navy hover:brightness-110" 
                      : "bg-itmunch-navy text-white hover:bg-itmunch-navy/90"
                  }`}
                  data-testid={`pricing-cta-${index}`}
                >
                  Get Started
                </Button>
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "iTMunch's SEO optimization services completely transformed our online presence. Within 6 months, we saw a 350% increase in organic traffic and doubled our conversion rate.",
      role: "VP of Marketing",
      industry: "Enterprise SaaS"
    },
    {
      quote: "Their data-driven approach and transparent reporting made all the difference. We now rank #1 for our most competitive keywords.",
      role: "Digital Director",
      industry: "E-commerce Retail"
    },
    {
      quote: "The ROI from their SEO services exceeded our expectations. Best investment we've made in our digital marketing strategy.",
      role: "CEO",
      industry: "FinTech Startup"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-itmunch-dark" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
            Client Success Stories
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Real results from businesses that chose our SEO optimization services.
          </p>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="glass-card rounded-2xl p-8 relative"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="text-itmunch-cyan/30 absolute top-6 left-6" size={40} />
              <p className="text-gray-300 mt-8 mb-6 relative z-10">
                "{testimonial.quote}"
              </p>
              <div className="border-t border-white/10 pt-4">
                <p className="text-white font-medium">{testimonial.role}</p>
                <p className="text-itmunch-cyan text-sm">{testimonial.industry}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "What SEO optimization services do you offer?",
      answer: "We offer comprehensive SEO optimization services including technical SEO audits, keyword research and strategy, on-page optimization, content creation and optimization, link building, local SEO, GEO (Generative Engine Optimization) for AI platforms, and ongoing performance monitoring. Our services leverage advanced automation while maintaining expert human oversight for strategic decision-making."
    },
    {
      question: "What is GEO and why is it important?",
      answer: "GEO (Generative Engine Optimization) is the practice of optimizing your content and brand presence to be recommended by AI platforms like ChatGPT, Gemini, Perplexity, and Claude. As more users rely on AI assistants for recommendations and information, having your brand mentioned by these platforms becomes crucial for discovery and lead generation. We help structure your content and build authority so AI models recognize and recommend your business."
    },
    {
      question: "How long does it take to see SEO results?",
      answer: "While some improvements can be seen within the first month, significant results typically appear within 3-6 months. SEO is a long-term strategy that builds momentum over time. Our data-driven approach ensures you see consistent progress, with most clients experiencing substantial traffic increases and improved rankings by month 6."
    },
    {
      question: "What is automation in SEO and how do you use it?",
      answer: "Automation in SEO refers to using advanced tools and technology to streamline repetitive tasks like technical audits, rank tracking, and performance monitoring. We combine cutting-edge automation with human expertise to deliver faster, more accurate results while ensuring strategic decisions are made by experienced SEO professionals."
    },
    {
      question: "How do your services improve ROI?",
      answer: "Our SEO optimization services improve ROI by increasing organic traffic, improving conversion rates, and reducing customer acquisition costs. Unlike paid advertising, SEO provides sustainable long-term results. Our clients typically see an average 300% ROI increase through higher search rankings, increased qualified traffic, and better conversion optimization."
    },
    {
      question: "How does the pricing for extra pages work?",
      answer: "Each plan includes a set number of pages. If your website requires optimization for more pages than your plan includes, we charge an additional $1 per page per month. For example, if you're on the Starter plan (50 pages included) but need 75 pages optimized, you'd pay an extra $25/month for the 25 additional pages."
    }
  ];

  return (
    <section id="faq" className="py-20 lg:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-itmunch-navy mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-500">
            Everything you need to know about our SEO optimization services.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-itmunch-light rounded-xl px-6 border-none faq-item"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-itmunch-navy font-medium hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-500 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
   const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    countryCode: "+1",
    phone: "",
    website: "",
    message: ""
  });
const [errors, setErrors] = useState({});
const [showPopup, setShowPopup] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
const [submitted, setSubmitted] = useState(false);
const [submitting, setSubmitting] = useState(false);
  // ✅ PUT IT HERE
 

 const handleChange = (e) => {
  const { name, value } = e.target;

  setFormData({
    ...formData,
    [name]: value
  });

  if (errors[name]) {
    setErrors({
      ...errors,
      [name]: ""
    });
  }
};

  const handleCountryCodeChange = (code) => {
    setFormData({ ...formData, countryCode: code });
  };

  const freeEmailDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com"
];

const validateForm = () => {
  let newErrors = {};

  const freeEmailDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "icloud.com"
  ];

  if (!formData.name.trim()) {
    newErrors.name = "Please enter your name.";
  }

  if (!formData.company.trim()) {
    newErrors.company = "Please enter your company name.";
  }

  if (!formData.phone.trim()) {
    newErrors.phone = "Please enter your phone number.";
  }

  if (!formData.website.trim()) {
    newErrors.website = "Please enter your website URL.";
  }

  if (!formData.message.trim()) {
    newErrors.message = "Please enter your message.";
  }

  if (!formData.email.trim()) {
    newErrors.email = "Please enter your email address";
  } else {
    const emailParts = formData.email.split("@");

    if (emailParts.length !== 2) {
      newErrors.email = "Invalid email format";
    } else {
      const domain = emailParts[1];

      if (freeEmailDomains.includes(domain)) {
        newErrors.email = "Please use your business email";
      }
    }
  }

  setErrors(newErrors);

  return Object.keys(newErrors).length === 0;
};

// const handleSubmit = async (e) => {
//   e.preventDefault();

//  if (!isFormValid) {
//   alert("Please fill all required fields.");
//   return;
// }

//   setSubmitting(true);

//   const formPayload = new FormData();
//   formPayload.append("name", formData.name);
//   formPayload.append("email", formData.email);
//   formPayload.append(
//     "phone",
//     `${formData.countryCode} ${formData.phone}`
//   );
//   formPayload.append("company", formData.company);
//   formPayload.append("website", formData.website);
//   formPayload.append("message", formData.message);

//   try {
//     const response = await fetch(
//       "https://itmunch.com/enterprise-seo-service-test/send-mail.php",
//       {
//         method: "POST",
//         body: formPayload,
//       }
//     );

//     if (!response.ok) throw new Error("Failed");

//     alert("Message sent successfully!");

//     setFormData({
//       name: "",
//       email: "",
//       company: "",
//       countryCode: "+1",
//       phone: "",
//       website: "",
//       message: ""
//     });

//   } catch (err) {
//     alert("Something went wrong.");
//     console.error(err);
//   }

//   setSubmitting(false);
// };


const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  setSubmitting(true);

  try {
    const response = await fetch(
      "https://itmunch.com/enterprise-seo-services/send-mail.php",
      {
        method: "POST",
        body: new URLSearchParams(formData),
      }
    );

    if (!response.ok) {
      throw new Error("Failed");
    }

    setShowSuccess(true);

  } catch (error) {
    alert("Something went wrong.");
  }

  setSubmitting(false);
};
  return (
    <section id="contact" className="py-20 lg:py-32 bg-itmunch-dark" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-6">
              Ready to Dominate Search Rankings?
            </h2>
            <p className="text-gray-400 mb-8">
              Get your free SEO consultation and discover how we can help your business 
              achieve sustainable growth through data-driven optimization.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-itmunch-cyan/10 rounded-xl flex items-center justify-center">
                  <Clock className="text-itmunch-cyan" size={24} />
                </div>
                <div>
                  <p className="text-white font-medium">Quick Response</p>
                  <p className="text-gray-400 text-sm">We respond within 24 hours</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-itmunch-cyan/10 rounded-xl flex items-center justify-center">
                  <Shield className="text-itmunch-cyan" size={24} />
                </div>
                <div>
                  <p className="text-white font-medium">No Obligation</p>
                  <p className="text-gray-400 text-sm">Free consultation, no strings attached</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-itmunch-cyan/10 rounded-xl flex items-center justify-center">
                  <Search className="text-itmunch-cyan" size={24} />
                </div>
                <div>
                  <p className="text-white font-medium">Custom Strategy</p>
                  <p className="text-gray-400 text-sm">Tailored SEO plan for your business</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Name *</label>
                  {/* <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 input-focus"
                    data-testid="contact-name-input"
                  /> */}
                  <Input
  name="name"
  value={formData.name}
  onChange={handleChange}
  placeholder="Name"
  className={`bg-white/5 border ${errors.name ? "border-red-500" : "border-white/10"} text-white`}
 />

{errors.name && (
  <p className="text-red-400 text-sm mt-1">{errors.name}</p>
)}
                </div>
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Email *</label>
                  <Input
  name="email"
  type="email"
  placeholder="Email"
  value={formData.email}
  onChange={handleChange}
  className={`bg-white/5 border ${errors.email ? "border-red-500" : "border-white/10"} text-white`}
 />

{errors.email && (
  <p className="text-red-400 text-sm mt-1">{errors.email}</p>
)}
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Company</label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company Name"
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 input-focus"
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Phone</label>
                  <div className="flex gap-2">
                    <CountryCodeSelect
                      value={formData.countryCode}
                      onChange={handleCountryCodeChange}
                    />
                    <Input
  name="phone"
  value={formData.phone}
  onChange={handleChange}
  placeholder="Phone"
  className={`flex-1 bg-white/5 border ${errors.phone ? "border-red-500" : "border-white/10"} text-white`}
/>

                  </div>
{errors.phone && (
  <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
)}
                </div>
              </div>
              <div className="mb-4">
                <label className="text-gray-300 text-sm mb-2 block">Website URL</label>
                <Input
  name="website"
  value={formData.website}
  onChange={handleChange}
  placeholder="Website Url"
  className={`bg-white/5 border ${errors.website ? "border-red-500" : "border-white/10"} text-white`}
/>

{errors.website && (
  <p className="text-red-400 text-sm mt-1">{errors.website}</p>
)}
              </div>

              <div className="mb-6">
                <label className="text-gray-300 text-sm mb-2 block">How can we help? *</label>
                <textarea
  name="message"
  value={formData.message}
  onChange={handleChange}
  placeholder="Your Message"
  rows={4}
  className={`w-full px-4 py-3 bg-white/5 border ${errors.message ? "border-red-500" : "border-white/10"} rounded-lg text-white`}
/>

{errors.message && (
  <p className="text-red-400 text-sm mt-1">{errors.message}</p>
)}
              </div>

              {/* <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-itmunch-cyan text-itmunch-navy hover:brightness-110 rounded-full py-6 text-lg btn-shine"
                data-testid="contact-submit-button"
              >
                {Submitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message <Send className="ml-2" size={20} />
                  </>
                )}
              </Button> */}
              {/* <Button
  type="submit"
  disabled={!isFormValid || submitting}
  className="w-full bg-itmunch-cyan text-itmunch-navy hover:brightness-110 rounded-full py-6 text-lg btn-shine"
>
  {submitting ? "Sending..." : "Send Message"}
</Button> */}
<Button
  type="submit"
   disabled={submitting}
  className="w-full bg-itmunch-cyan text-itmunch-navy rounded-full py-6 text-lg"
>
  {/* {submitting ? (
    <span className="flex items-center justify-center gap-2">
      Sending...
      <Send size={18} />
    </span>
  ) : (
    "Send Message"
  )} */}
  {submitting ? "Sending..." : "Send Message"}
</Button>
            </form>
          </motion.div>
        </div>
      </div>
      {showSuccess && (
  <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
    <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-xl">

      <div className="text-5xl mb-4">👍</div>

      <h3 className="text-2xl font-semibold mb-2">
        Message Sent Successfully!
      </h3>

      <p className="text-gray-600 mb-6">
        Thank you for contacting us. Our team will get back to you shortly.
      </p>

      <button
       onClick={() => {
  setShowSuccess(false);

  setFormData({
    name: "",
    email: "",
    company: "",
    countryCode: "+1",
    phone: "",
    website: "",
    message: ""
  });

  setErrors({});
}}
        className="bg-itmunch-cyan text-white px-6 py-2 rounded-full hover:brightness-110"
      >
        Close
      </button>

    </div>
  </div>
)}
    </section>
  );
};

// Footer
const Footer = () => {
  const services = [
    { name: "Enterprise SEO", href: "https://itmunch.com/enterprise-seo-services/" },
    { name: "Content Syndication", href: "https://itmunch.com/b2b-content-syndication-services/" },
    { name: "B2B Lead Generation", href: "https://itmunch.com/b2b-lead-generation-services/" },
    { name: "Email Marketing", href: "https://itmunch.com/email-marketing-services/" },
    { name: "Meta Advertising", href: "https://itmunch.com/meta-advertising-services/" },
    { name: "Programmatic Ads", href: "https://itmunch.com/programmatic-advertising-services/" },
    { name: "Backlink Services", href: "https://backlink.itmunch.com/" },
    { name: "AI Agent Services", href: "https://itmunch.com/ai-agent-for-sales/" },
  ];

  const socialLinks = [
    { name: "Facebook", href: "https://www.facebook.com/iTMunch", icon: "facebook" },
    { name: "Twitter", href: "https://x.com/it_munch", icon: "twitter" },
    { name: "LinkedIn", href: "https://www.linkedin.com/company/13435249/", icon: "linkedin" },
    { name: "Instagram", href: "https://www.instagram.com/it_munch/", icon: "instagram" },
  ];

  return (
    <footer className="bg-itmunch-navy pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <div className="mb-4">
            <a href="https://itmunch.com">
              <img 
                src="https://itmunch.com/wp-content/uploads/2021/01/ITT-MUNCH_LOGO-4-1-1024x512_800x400.webp" 
                alt="iTMunch - Where Technology Meets You"
                className="h-24 w-auto"
              /></a>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your trusted partner for enterprise-grade SEO and digital marketing solutions.
            </p>
            <a 
              href="mailto:connect@itmunch.com" 
              className="text-itmunch-cyan hover:text-itmunch-accent transition-colors flex items-center gap-2"
              data-testid="footer-email"
            >
              <Mail size={18} />
              connect@itmunch.com
            </a>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-heading font-semibold mb-4">Our Services</h4>
            <div className="grid grid-cols-2 gap-3">
              {services.map((service, index) => (
                <a
                  key={index}
                  href={service.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-itmunch-cyan transition-colors text-sm"
                  data-testid={`footer-service-${index}`}
                >
                  {service.name}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links & Social */}
          <div>
            <h4 className="text-white font-heading font-semibold mb-4">Quick Links</h4>
            <div className="flex flex-col gap-3 mb-6">
              <a href="#services" className="text-gray-400 hover:text-itmunch-cyan transition-colors text-sm">Services</a>
              <a href="#pricing" className="text-gray-400 hover:text-itmunch-cyan transition-colors text-sm">Pricing</a>
              <a href="#faq" className="text-gray-400 hover:text-itmunch-cyan transition-colors text-sm">FAQ</a>
              <a href="#contact" className="text-gray-400 hover:text-itmunch-cyan transition-colors text-sm">Contact Us</a>
            </div>

            <h4 className="text-white font-heading font-semibold mb-4">Follow Us</h4>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 hover:bg-itmunch-cyan/20 rounded-lg flex items-center justify-center text-gray-400 hover:text-itmunch-cyan transition-all"
                  data-testid={`footer-social-${social.icon}`}
                  aria-label={social.name}
                >
                  {social.icon === "facebook" && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  )}
                  {social.icon === "twitter" && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  )}
                  {social.icon === "linkedin" && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  )}
                  {social.icon === "instagram" && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} iTMunch. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="https://itmunch.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-itmunch-cyan transition-colors">Privacy Policy</a>
              <a href="https://itmunch.com/terms-conditions/" target="_blank" rel="noopener noreferrer" className="hover:text-itmunch-cyan transition-colors">Terms of Use</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <main>
      <Navigation />
      <HeroSection />
      <StatsSection />
      <ServicesSection />
      <GEOSection />
      <ProcessSection />
      <WhyChooseSection />
      <PricingSection />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />
      <Footer />
    </main>
  );
};

export default LandingPage;
